<template>
  <Modal
    class="instrument-modal"
    data-testid="instrument-modal"
    :is-open="isOpen"
    size="md"
    title="Selecionar ativo"
    v-on="$listeners"
  >
    <div class="instrument-modal__content">
      <template v-if="isErrorStatus">
        <HelperViewStatus
          data-testid="instrument-modal-error"
          class="instrument-modal__helper"
          status="alert"
          button-label="Tentar novamente"
          title="Desculpe, não conseguimos carregar os seus ativos"
          description="Aguarde um momento e tente novamente"
          @click="emitOnRetry"
        />
      </template>
      <template v-else>
        <InputText
          v-model="value"
          leading-icon="EE0150"
          :trailing-icon="inputTrailingIcon"
          icon-has-hover
          @input="handleOnInputDebounced"
          @icon-click="clearInputText"
        />
        <div v-if="isDefaultStatus">
          <div :class="fixedFiltersClass">
            <InstrumentOption
              :value="InstrumentSelectionOptions.All"
              symbol="Todos ativos"
              :is-selected="isAllInstrumentOptionSelected"
              @on-select="handleOnSelect"
            />
          </div>
          <BaseText
            v-if="resultLabel"
            class="instrument-modal__result-label"
            size="md"
            :title="resultLabel"
            >{{ resultLabel }}</BaseText
          >
          <InfiniteScroll
            class="instrument-modal__results"
            :max-per-page="itemsPerPage"
            :request="handleFetchMoreOptions"
            :item-list="instruments"
            :initial-page="1"
            data-testid="infinite-scroll"
            not-found-items-message="Nenhum ativo encontrado"
            whitelabel
          >
            <template #default="item">
              <InstrumentOption
                :key="item.symbol"
                :name="item.description"
                :symbol="item.symbol"
                :value="item.symbol"
                :type="item.type"
                :is-selected="item.symbol === currentSelectedOption"
                @on-select="handleOnSelect"
              />
            </template>
          </InfiniteScroll>
        </div>
        <div v-else-if="isLoadingStatus">
          <SkeletonLoader data-testid="instrument-modal-loading">
            <div
              v-for="index in 4"
              :key="index"
              class="instrument-modal__option-loader"
            />
          </SkeletonLoader>
        </div>
      </template>
    </div>
    <template v-if="!isErrorStatus" #buttons>
      <ButtonSecondary
        class="instrument-modal__button"
        label="Cancelar"
        size="large"
        :disabled="isLoadingStatus"
        @click="emitCancel"
      />
      <ButtonPrimary
        class="instrument-modal__button"
        label="Aplicar"
        size="large"
        :disabled="!isFilterButtonEnabled"
        @click="emitOnInstrumentSelection"
      />
    </template>
  </Modal>
</template>

<script lang="ts">
import { Component, Vue, Prop, Emit, Watch } from 'vue-property-decorator';
import { debounce } from 'lodash-es';

import { InstrumentOption } from './components/InstrumentOption';
import {
  Modal,
  InputText,
  ButtonPrimary,
  ButtonSecondary,
  HelperViewStatus,
  SkeletonLoader,
  InfiniteScroll,
  BaseText
} from '@warrenbrasil/nebraska-web';

import { InstrumentFilter } from '@/modules/trade/services/orders';
import { InstrumentSelectionOptions } from './types';
import {
  addBodyScrollWithScrollTo,
  removeBodyScroll
} from '@/modules/common/helpers/browser';
import { InstrumentFilterStatus } from '@/modules/trade/components/InstrumentFilter/types';

@Component({
  components: {
    BaseText,
    Modal,
    ButtonPrimary,
    ButtonSecondary,
    InputText,
    InstrumentOption,
    HelperViewStatus,
    SkeletonLoader,
    InfiniteScroll
  }
})
export default class InstrumentSelectionModal extends Vue {
  @Prop({ type: Boolean, default: false })
  readonly isOpen!: boolean;

  @Prop({ type: Array, required: true })
  readonly instruments!: InstrumentFilter[];

  @Prop({ type: [String, Number], required: true })
  readonly selectedOption!: string | number;

  @Prop({
    type: String,
    validator: (status: InstrumentFilterStatus) => {
      return Object.values(InstrumentFilterStatus).includes(status);
    },
    default: InstrumentFilterStatus.Default
  })
  readonly status!: InstrumentFilterStatus;

  @Prop({ type: String, required: false })
  readonly resultLabel?: string;

  @Prop({ type: Function, required: false })
  readonly fetchOptions?: (text: string, page: number) => {};

  currentSelectedOption: string | number = '';
  handleOnInputDebounced = debounce(this.handleOnInput, 600);
  value = '';
  prevValue = '';
  currentPage = 1;
  itemsPerPage = 10;

  readonly InstrumentSelectionOptions = InstrumentSelectionOptions;
  readonly InstrumentFilterStatus = InstrumentFilterStatus;

  @Emit('close')
  emitClose() {}

  @Emit('cancel')
  emitCancel() {}

  @Emit('on-instrument-selection')
  emitOnInstrumentSelection() {
    return this.currentSelectedOption;
  }

  @Emit('on-search')
  emitOnSearch(_text: string) {}

  @Emit('on-retry')
  emitOnRetry() {}

  @Emit('on-load-more')
  emitOnLoadMore() {}

  @Watch('selectedOption', { immediate: true })
  watchSelectedOption(selectedOption: string | number) {
    this.currentSelectedOption = selectedOption;
  }

  @Watch('isOpen', { immediate: true })
  watchIsOpen(isOpen: boolean) {
    if (isOpen) {
      removeBodyScroll();
    } else {
      this.value = '';
      this.currentSelectedOption = this.selectedOption;
      addBodyScrollWithScrollTo();
    }
  }

  get fixedFiltersClass() {
    return {
      'instrument-modal__fixed-filters': true,
      'instrument-modal__fixed-filters--bottom-spacing': !this.resultLabel
    };
  }

  get isFilterButtonEnabled() {
    return (
      this.selectedOption !== this.currentSelectedOption &&
      !this.isLoadingStatus
    );
  }

  get inputTrailingIcon() {
    return this.value.length > 0 ? 'ic_clear' : '';
  }

  get isAllInstrumentOptionSelected() {
    return this.currentSelectedOption === InstrumentSelectionOptions.All;
  }

  get isLoadingStatus() {
    return this.status === InstrumentFilterStatus.Loading;
  }

  get isErrorStatus() {
    return this.status === InstrumentFilterStatus.Error;
  }

  get isDefaultStatus() {
    return this.status === InstrumentFilterStatus.Default;
  }

  async handleFetchMoreOptions() {
    const isSearchStringSameAsPrevious = this.value === this.prevValue;
    if (isSearchStringSameAsPrevious) {
      this.currentPage++;
    } else {
      this.currentPage = 2;
    }

    const prevFetchedPage = this.currentPage - 1;
    const hasMoreItemsToFetch =
      this.instruments.length >= prevFetchedPage * this.itemsPerPage;
    this.prevValue = this.value;

    if (!hasMoreItemsToFetch) {
      return;
    }

    if (this.fetchOptions)
      await this.fetchOptions(this.value, this.currentPage);
  }

  handleOnSelect(value: string | number) {
    this.currentSelectedOption = value;
  }

  handleOnInput(value: string) {
    this.emitOnSearch(value);
  }

  clearInputText() {
    this.value = '';
    this.emitOnSearch(this.value);
  }
}
</script>

<style lang="less" scoped>
.instrument-modal {
  &__content {
    width: 100%;
  }

  &__helper {
    margin: 0 auto;
  }

  &__result-label {
    font-weight: bold;
    text-align: left;
    margin: @size-spacing-x400;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-all;
    white-space: normal;
  }

  &__results {
    /deep/ .loader {
      background-color: transparent !important;
    }
  }

  &__fixed-filters {
    margin-top: @size-spacing-x500;

    &--bottom-spacing {
      margin-bottom: @size-spacing-x500;
    }
  }

  &__option-loader {
    height: 33px;
    width: 100%;
    margin-bottom: @size-spacing-x600;
    border-radius: @size-radius-x200;

    &:first-of-type {
      margin-top: @size-spacing-x700;
      margin-bottom: @size-spacing-x900;
    }

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  &__button {
    &:first-child {
      margin-right: @size-spacing-x450;
    }
  }
}
</style>
